import { ZONE_RADIUS_METERS, type ZoneGeoJsonPoint, type ZoneGeoJsonPolygon } from '@chukua/common';
import { polygonCentroid } from './polygon-centroid';

/** What is drawn on the map for a zone: its polygon (with the centroid derived from it), or a centroid alone. */
export interface ZoneGeometry {
  polygon: ZoneGeoJsonPolygon | null;
  centroid: ZoneGeoJsonPoint | null;
}

/** A zone's shape as the form holds it: the geometry, plus a point zone's radius. All optional: a zone can be saved before it is placed. */
export interface ZoneShapeValue extends ZoneGeometry {
  radiusMeters: number | null;
}

/**
 * The shape once the geometry on the map changes. Only a point has a radius: placing one keeps the radius it
 * had, or starts from the default; drawing a polygon or clearing the shape drops it.
 */
export function withRadius(geometry: ZoneGeometry, previousRadiusMeters: number | null): ZoneShapeValue {
  const isPoint = !geometry.polygon && Boolean(geometry.centroid);
  return { ...geometry, radiusMeters: isPoint ? (previousRadiusMeters ?? ZONE_RADIUS_METERS.default) : null };
}

/** The terra-draw modes a zone is drawn with; `select` edits the shape already there. */
export type ZoneDrawMode = 'select' | 'polygon' | 'point';

/** The slice of a terra-draw store feature these rules read. */
export interface DrawnFeature {
  id?: string | number;
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

/**
 * What terra-draw loads for a zone. A zone has one shape: its polygon (the centroid is derived from it,
 * so it isn't drawn), or its centroid alone for a named point. `properties.mode` names the mode that owns
 * the feature; terra-draw silently drops a loaded feature without it.
 */
export function zoneDrawFeatures({ polygon, centroid }: ZoneGeometry): DrawnFeature[] {
  if (polygon) return [{ type: 'Feature', geometry: polygon, properties: { mode: 'polygon' } } as DrawnFeature];
  if (centroid) return [{ type: 'Feature', geometry: centroid, properties: { mode: 'point' } } as DrawnFeature];
  return [];
}

/** Only the zone's own shapes: terra-draw's snapshot also holds its selection handles and midpoints. */
function shapesOf(snapshot: DrawnFeature[]): DrawnFeature[] {
  return snapshot.filter((feature) => feature.properties.mode === 'polygon' || feature.properties.mode === 'point');
}

/** The geometry on the map now. A polygon's centroid is recomputed from it whenever it changes. */
export function zoneGeometryFromSnapshot(snapshot: DrawnFeature[]): ZoneGeometry {
  const shapes = shapesOf(snapshot);
  const polygon = shapes.find((feature) => feature.geometry.type === 'Polygon')?.geometry as ZoneGeoJsonPolygon | undefined;
  if (polygon) return { polygon, centroid: polygonCentroid(polygon) };
  const point = shapes.find((feature) => feature.geometry.type === 'Point')?.geometry as ZoneGeoJsonPoint | undefined;
  return { polygon: null, centroid: point ?? null };
}

/** Once a new shape is finished, every other one is replaced by it: drawing a polygon over a point swaps it, and so on. */
export function shapesReplacedBy(snapshot: DrawnFeature[], finishedId: string | number): (string | number)[] {
  return shapesOf(snapshot)
    .map((feature) => feature.id)
    .filter((id): id is string | number => id !== undefined && id !== finishedId);
}

/** A zone that already has a shape opens ready to edit it; one without opens ready to draw a polygon. */
export function initialDrawMode(value: ZoneGeometry): ZoneDrawMode {
  return value.polygon || value.centroid ? 'select' : 'polygon';
}
